function auctionFormatMoney(cents, format) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '';

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const defaultFormat = '${{amount}}';

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (Number.isNaN(number)) return 0;

    const negative = number < 0 ? '-' : '';
    const parts = (Math.abs(number) / 100).toFixed(precision).split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return `${negative}${dollarsAmount}${centsAmount}`;
  }

  let value = '';
  const matches = (format || defaultFormat).match(placeholderRegex);
  const formatType = matches ? matches[1] : 'amount';

  switch (formatType) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return (format || defaultFormat).replace(placeholderRegex, value);
}

// Deliberately simple (not full RFC 5322) - just enough to catch typos/junk
// before it reaches the backend, matching what type="email" would enforce
// natively if the form didn't use novalidate (needed so failures render as the
// theme's own error-message UI instead of the browser's native bubble).
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function auctionFillTemplate(template, tokens) {
  let result = template;
  Object.keys(tokens).forEach((key) => {
    result = result.replace(`%%${key}%%`, tokens[key]);
  });
  return result;
}

/**
 * Formats an ISO instant for display, always in the VIEWER's own local
 * timezone (each browser converts independently - a shopper in Lisbon and
 * one in Helsinki each see the correct time on their own clock from the
 * same stored UTC value, automatically). timeZoneName is included explicitly
 * so that's never ambiguous - without it, a merchant double-checking a
 * scheduled date, or the exact viewer who entered it, has no way to tell
 * whether a wrong-looking time is a real mismatch (e.g. the metafield's
 * date-time picker having interpreted the entry in a different timezone
 * than intended) or just their own local offset from what someone else saw.
 */
function auctionFormatDateTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // dateStyle/timeStyle can't be combined with timeZoneName (throws a
  // RangeError - they're mutually exclusive per the Intl spec), so this
  // spells out the individual fields instead to get the same "Aug 19, 2026,
  // 6:00 PM GMT+2" shape reliably.
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Bidder identity helper. The Shopify app proxy strips Set-Cookie/Cookie headers, so
 * we can't use cookies. Instead the backend returns a signed identity token which we
 * persist in localStorage and send back as the `bt` query param on every request
 * (Shopify signs client-supplied query params, so the app-proxy signature still
 * validates).
 */
const AUCTION_TOKEN_KEY = 'auction_bidder_token';

// Populated by AuctionIdentity.captureFromUrl() from the auctionConfirmed/auctionError
// query params the email-confirm redirect carries (see getConfirm.ts /
// confirmBidderAndApplyPendingBid.ts) - read once by <auction-lot> on connect so it can
// show a one-time banner, then cleared so it never reappears on a later reconnect.
const AuctionRedirectOutcome = {
  confirmed: false,
  errorCode: null,
};

const AuctionIdentity = {
  get() {
    try {
      return localStorage.getItem(AUCTION_TOKEN_KEY) || '';
    } catch (e) {
      return '';
    }
  },

  set(token) {
    if (!token) return;
    try {
      localStorage.setItem(AUCTION_TOKEN_KEY, token);
    } catch (e) {
      /* storage unavailable (private mode) - identity just won't persist */
    }
  },

  /** Appends the stored bidder token as the `bt` query param, if we have one. */
  withToken(url) {
    const token = this.get();
    if (!token) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}bt=${encodeURIComponent(token)}`;
  },

  /**
   * The email confirm redirect hands the identity back as a `bt` URL param, plus
   * auctionConfirmed=1 (bid applied) or auctionError=<code> (email confirmed but
   * the pending bid itself failed - e.g. someone else outbid it, or the auction
   * ended, while it was waiting on confirmation). Capture all three into
   * localStorage/AuctionRedirectOutcome, then strip them from the address bar so
   * they aren't shared and don't reappear on refresh.
   */
  captureFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      const bt = params.get('bt');
      const confirmed = params.get('auctionConfirmed');
      const errorCode = params.get('auctionError');
      if (!bt && !confirmed && !errorCode) return;

      if (bt) {
        this.set(bt);
        params.delete('bt');
      }
      if (confirmed) {
        AuctionRedirectOutcome.confirmed = true;
        params.delete('auctionConfirmed');
      }
      if (errorCode) {
        AuctionRedirectOutcome.errorCode = errorCode;
        params.delete('auctionError');
      }

      const qs = params.toString();
      const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    } catch (e) {
      /* no-op */
    }
  },
};

AuctionIdentity.captureFromUrl();

// Below this remaining time, the timer pill shifts toward a warmer color (see
// .auction__timer--urgent) - a quiet visual cue that time is running low,
// deliberately understated rather than a blinking/alarming treatment.
const AUCTION_TIMER_URGENT_THRESHOLD_MS = 5 * 60 * 1000;

if (!customElements.get('auction-timer')) {
  customElements.define(
    'auction-timer',
    class AuctionTimer extends HTMLElement {
      constructor() {
        super();
        this.valueEl = this.querySelector('[data-auction-timer-value]');
        this.endsAt = null;
        this.interval = null;
      }

      setEndsAt(iso) {
        const next = Date.parse(iso);
        if (Number.isNaN(next)) return;

        if (this.endsAt !== null && next > this.endsAt) {
          this.classList.add('auction__timer--extended');
          clearTimeout(this._extendedTimeout);
          this._extendedTimeout = setTimeout(() => this.classList.remove('auction__timer--extended'), 4000);
        }

        this.endsAt = next;

        if (!this.interval) {
          this.tick();
          this.interval = setInterval(this.tick.bind(this), 1000);
        }
      }

      tick() {
        const remaining = this.endsAt - Date.now();

        if (remaining <= 0) {
          this.valueEl.textContent = '00:00:00';
          this.classList.remove('auction__timer--urgent');
          clearInterval(this.interval);
          this.interval = null;
          this.dispatchEvent(new CustomEvent('auction:timer-expired', { bubbles: true }));
          return;
        }

        this.valueEl.textContent = AuctionTimer.formatRemaining(remaining);
        this.classList.toggle('auction__timer--urgent', remaining <= AUCTION_TIMER_URGENT_THRESHOLD_MS);
      }

      static formatRemaining(ms) {
        const totalSeconds = Math.floor(ms / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const pad = (n) => String(n).padStart(2, '0');

        if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }

      disconnectedCallback() {
        clearInterval(this.interval);
        // Reset to null (not just cleared) - setEndsAt() only restarts the
        // interval when this is falsy, so leaving a stale id here would
        // permanently freeze the countdown the moment this element is ever
        // reparented (e.g. the auction-list grid re-sorting itself) and then
        // reconnected, since disconnectedCallback/connectedCallback fire on
        // every custom element in a moved subtree even when its DOM position
        // doesn't actually change.
        this.interval = null;
        clearTimeout(this._extendedTimeout);
      }
    }
  );
}

if (!customElements.get('bid-form')) {
  customElements.define(
    'bid-form',
    class BidForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('[data-auction-form]');
        this.amountInput = this.querySelector('[data-auction-amount-input]');
        this.nameField = this.querySelector('[data-auction-name-field]');
        this.nameInput = this.querySelector('[data-auction-name-input]');
        this.emailField = this.querySelector('[data-auction-email-field]');
        this.emailInput = this.querySelector('[data-auction-email-input]');
        this.notifyInput = this.querySelector('[data-auction-notify-input]');
        this.submitButton = this.querySelector('[data-auction-submit-button]');
        this.spinner = this.submitButton.querySelector('.loading-overlay__spinner');
        this.errorWrapper = this.querySelector('[data-auction-bid-error]');
        this.errorVariants = {
          amountRequired: this.querySelector('[data-auction-error="amountRequired"]'),
          bidTooLow: this.querySelector('[data-auction-error="bidTooLow"]'),
          auctionEnded: this.querySelector('[data-auction-error="auctionEnded"]'),
          auctionNotStarted: this.querySelector('[data-auction-error="auctionNotStarted"]'),
          nameRequired: this.querySelector('[data-auction-error="nameRequired"]'),
          emailRequired: this.querySelector('[data-auction-error="emailRequired"]'),
          invalidEmail: this.querySelector('[data-auction-error="invalidEmail"]'),
          generic: this.querySelector('[data-auction-error="generic"]'),
        };
        this.bidTooLowTemplate = this.querySelector('[data-auction-bid-too-low-template]').textContent;
        this.quickBidsEl = this.querySelector('[data-auction-quick-bids]');
        this.pendingEl = this.querySelector('[data-auction-pending-confirmation]');
        this.pendingTemplate = this.querySelector('[data-auction-pending-confirmation-template]').textContent;
        this.modeInfoEl = this.querySelector('[data-auction-mode-info]');
        this.modeRadios = Array.from(this.querySelectorAll('[data-auction-mode-radio]'));

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.modeRadios.forEach((radio) => radio.addEventListener('change', this.onModeChange.bind(this)));
        this.notifyInput.addEventListener('change', () => {
          this.notifyTouched = true;
        });

        const presets = this.closest('auction-lot').dataset.bidIncrementPresets || '1,2,5';
        this.presetMultipliers = presets
          .split(',')
          .map((n) => parseFloat(n))
          .filter((n) => !Number.isNaN(n) && n > 0);

        this.onModeChange();
      }

      onModeChange() {
        const isMax = this.modeRadios.find((radio) => radio.checked)?.value === 'max';
        this.modeInfoEl.classList.toggle('hidden', !isMax);
      }

      updateFromState(state) {
        // Bids are whole-euro amounts only - round the minimum up so it's never
        // below the true (possibly fractional) minNextBid the backend computed.
        this.minNextBid = Math.ceil(state.minNextBid);
        this.amountInput.min = this.minNextBid;
        if (!this.amountInput.value) this.amountInput.value = this.minNextBid;

        this.renderQuickBids(this.minNextBid);

        this.nameField.hidden = Boolean(state.yourBidderTokenConfirmed);
        this.emailField.hidden = Boolean(state.yourBidderTokenConfirmed);
        // Every poll re-syncs this from the server's stored preference - skip
        // that once the shopper has touched the checkbox themselves, otherwise
        // checking it and not submitting immediately gets silently reverted on
        // the next poll cycle (the stored value doesn't change until they bid).
        if (!this.notifyTouched) this.notifyInput.checked = Boolean(state.yourOutbidNotificationsEnabled);

        // A still-unconfirmed bidder already gave us their name/email on an
        // earlier bid (stored on their pending Bidder record) - prefill rather
        // than making them retype it on every subsequent bid before they've
        // even clicked the confirm link. Never overwrites something they're
        // actively typing.
        if (!this.nameInput.value && state.yourName) this.nameInput.value = state.yourName;
        if (!this.emailInput.value && state.yourEmail) this.emailInput.value = state.yourEmail;

        const isLive = state.status === 'live';
        this.submitButton.disabled = !isLive;
        this.form.querySelectorAll('input').forEach((input) => (input.disabled = !isLive));
      }

      renderQuickBids(minNextBid) {
        const moneyFormat = this.closest('auction-lot').dataset.moneyFormat;
        this.quickBidsEl.innerHTML = '';
        this.presetMultipliers.forEach((multiplier) => {
          const amount = Math.round(minNextBid * multiplier);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'auction__quick-bid-button';
          button.textContent = auctionFormatMoney(amount * 100, moneyFormat);
          button.addEventListener('click', () => {
            this.amountInput.value = amount;
          });
          this.quickBidsEl.appendChild(button);
        });
      }

      setError(variant) {
        this.errorWrapper.hidden = !variant;
        Object.keys(this.errorVariants).forEach((key) => {
          this.errorVariants[key].hidden = key !== variant;
        });
      }

      onSubmit(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.setError(null);

        if (!this.nameField.hidden && !this.nameInput.value.trim()) {
          this.setError('nameRequired');
          this.nameInput.focus();
          return;
        }

        if (!this.emailField.hidden) {
          if (!this.emailInput.value.trim()) {
            this.setError('emailRequired');
            this.emailInput.focus();
            return;
          }
          if (!EMAIL_PATTERN.test(this.emailInput.value.trim())) {
            this.setError('invalidEmail');
            this.emailInput.focus();
            return;
          }
        }

        if (!this.amountInput.value.trim()) {
          this.setError('amountRequired');
          this.amountInput.focus();
          return;
        }

        // Bids are whole-euro amounts only - round rather than reject, so a
        // pasted or scrolled-in fractional value doesn't just error out.
        const amount = Math.round(parseFloat(this.amountInput.value));
        if (!Number.isFinite(amount) || amount < this.minNextBid) {
          this.showBidTooLowError(this.minNextBid);
          this.amountInput.focus();
          return;
        }

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.spinner.classList.remove('hidden');

        const mode = this.modeRadios.find((radio) => radio.checked)?.value || 'raise';
        const body = { amount, mode, notifyOnOutbid: this.notifyInput.checked };
        if (!this.nameField.hidden) body.name = this.nameInput.value.trim();
        if (!this.emailField.hidden) body.email = this.emailInput.value;

        const auctionLot = this.closest('auction-lot');

        fetch(AuctionIdentity.withToken(auctionLot.dataset.bidUrl), {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
          .then(({ ok, data }) => {
            if (data.bidderToken) AuctionIdentity.set(data.bidderToken);

            if (data.pendingConfirmation) {
              this.pendingEl.hidden = false;
              this.pendingEl.textContent = auctionFillTemplate(this.pendingTemplate, { EMAIL: body.email });
              return;
            }

            if (!ok) {
              this.handleError(data);
              // Refetch so the page flips over to the real current state
              // (ended, or still-scheduled) instead of leaving a stale form
              // a shopper could just try submitting again.
              if (data.error === 'auctionEnded' || data.error === 'auctionNotStarted') auctionLot.fetchState();
              return;
            }

            this.pendingEl.hidden = true;
            // The POST response already IS the fresh lot state (buildLotState) -
            // render it directly instead of firing a second GET, so the price/
            // history update immediately and can't lose a race with an
            // eventually-consistent read on a separate request.
            auctionLot.render(data);
            auctionLot.showBanner('success', auctionLot.bannerVariants.bidPlaced);
          })
          .catch(() => {
            this.setError('generic');
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-disabled');
            this.spinner.classList.add('hidden');
          });
      }

      showBidTooLowError(minNextBid) {
        this.errorVariants.bidTooLow.textContent = auctionFillTemplate(this.bidTooLowTemplate, {
          MIN_BID: auctionFormatMoney(Math.round(minNextBid * 100), this.closest('auction-lot').dataset.moneyFormat),
        });
        this.setError('bidTooLow');
      }

      handleError(data) {
        if (data.error === 'bidTooLow') {
          // The server just recomputed this from the live price - prefer it over
          // our own (possibly one poll cycle stale) minNextBid.
          this.showBidTooLowError(data.minNextBid ?? this.minNextBid);
          return;
        }
        if (data.error === 'auctionEnded') {
          this.setError('auctionEnded');
          return;
        }
        if (data.error === 'auctionNotStarted') {
          this.setError('auctionNotStarted');
          return;
        }
        this.setError('generic');
      }
    }
  );
}

if (!customElements.get('auction-lot')) {
  customElements.define(
    'auction-lot',
    class AuctionLot extends HTMLElement {
      constructor() {
        super();
        this.bannerEl = this.querySelector('[data-auction-redirect-banner]');
        this.bannerMessageEl = this.querySelector('[data-auction-redirect-banner-message]');
        this.bannerDismissButton = this.querySelector('[data-auction-redirect-banner-dismiss]');
        this.bannerVariants = {
          confirmed: this.querySelector('[data-auction-banner-variant="confirmed"]')?.textContent,
          bidPlaced: this.querySelector('[data-auction-banner-variant="bidPlaced"]')?.textContent,
          bidTooLow: this.querySelector('[data-auction-banner-variant="bidTooLow"]')?.textContent,
          auctionEnded: this.querySelector('[data-auction-banner-variant="auctionEnded"]')?.textContent,
          generic: this.querySelector('[data-auction-banner-variant="generic"]')?.textContent,
        };
        this.onBannerDismiss = this.onBannerDismiss.bind(this);

        this.loadingEl = this.querySelector('[data-auction-loading]');
        this.contentEl = this.querySelector('[data-auction-content]');
        this.endStateEl = this.querySelector('[data-auction-end-state]');
        this.endStateVariants = {
          sold: this.querySelector('[data-auction-end-state-variant="sold"]'),
          scheduled: this.querySelector('[data-auction-end-state-variant="scheduled"]'),
          not_sold: this.querySelector('[data-auction-end-state-variant="not_sold"]'),
          no_bids: this.querySelector('[data-auction-end-state-variant="no_bids"]'),
          paused: this.querySelector('[data-auction-end-state-variant="paused"]'),
        };
        this.soldBannerSubtitleEl = this.querySelector('[data-auction-end-banner-subtitle]');
        this.soldToTemplate = this.querySelector('[data-auction-sold-to-template]').textContent;
        this.notStartedTemplate = this.querySelector('[data-auction-not-started-template]').textContent;
        this.endedOnTemplate = this.querySelector('[data-auction-ended-on-template]').textContent;
        this.currentBidEl = this.querySelector('[data-auction-current-bid]');
        this.yourBidEl = this.querySelector('[data-auction-your-bid]');
        this.yourBidTemplate = this.querySelector('[data-auction-your-bid-template]').textContent;
        this.leadingNoteEl = this.querySelector('[data-auction-leading-note]');
        this.leadingNoteTextEl = this.querySelector('[data-auction-leading-note-text]');
        this.leadingNoteTemplate = this.querySelector('[data-auction-leading-note-template]').textContent;
        this.bidCountEl = this.querySelector('[data-auction-bid-count]');
        this.bidCountTemplate = this.querySelector('[data-auction-bid-count-template]').textContent;
        this.watcherCountEl = this.querySelector('[data-auction-watcher-count]');
        this.watchingTemplate = this.querySelector('[data-auction-watching-template]').textContent;
        this.watchButton = this.querySelector('[data-auction-watch-button]');
        this.timerEl = this.querySelector('[data-auction-timer]');
        this.endDateEl = this.querySelector('[data-auction-end-date]');
        this.endDateTemplate = this.querySelector('[data-auction-end-date-template]').textContent;
        this.bidFormEl = this.querySelector('[data-auction-bid-form]');
        this.historyListEl = this.querySelector('[data-auction-history-list]');
        this.historyRowTemplate = this.querySelector('[data-auction-history-row-template]');
        this.historyNoteEl = this.querySelector('[data-auction-history-note]');
        this.historyNoteVariants = {
          sole: this.querySelector('[data-auction-history-note-variant="sole"]')?.textContent,
          uncontested: this.querySelector('[data-auction-history-note-variant="uncontested"]')?.textContent,
        };

        this.pollIntervalMs = (parseInt(this.dataset.pollInterval, 10) || 4) * 1000;
        this.pollTimer = null;

        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        this.onTimerExpired = this.onTimerExpired.bind(this);
        this.onWatchClick = this.onWatchClick.bind(this);
      }

      connectedCallback() {
        this.showRedirectBanner();
        this.fetchState();
        this.startPolling();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.addEventListener('auction:timer-expired', this.onTimerExpired);
        this.watchButton.addEventListener('click', this.onWatchClick);
        this.bannerDismissButton.addEventListener('click', this.onBannerDismiss);
      }

      disconnectedCallback() {
        this.stopPolling();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.removeEventListener('auction:timer-expired', this.onTimerExpired);
        this.watchButton.removeEventListener('click', this.onWatchClick);
        this.bannerDismissButton.removeEventListener('click', this.onBannerDismiss);
      }

      /**
       * One-time banner for the email-confirm redirect - otherwise a shopper who
       * just confirmed their first bid lands back on the product page with no
       * acknowledgement anything happened (and, on a bidTooLow/auctionEnded
       * outcome, no explanation why their bid isn't in the history below).
       */
      showRedirectBanner() {
        if (AuctionRedirectOutcome.confirmed) {
          this.showBanner('success', this.bannerVariants.confirmed);
        } else if (AuctionRedirectOutcome.errorCode) {
          this.showBanner('error', this.bannerVariants[AuctionRedirectOutcome.errorCode] || this.bannerVariants.generic);
        } else {
          return;
        }

        // Consume it - a later reconnect (e.g. section reload in the theme editor)
        // shouldn't keep re-showing a banner for a redirect that already happened.
        AuctionRedirectOutcome.confirmed = false;
        AuctionRedirectOutcome.errorCode = null;
      }

      /** Also called directly by <bid-form> to confirm a successful direct bid. */
      showBanner(variant, message) {
        this.bannerMessageEl.textContent = message;
        this.bannerEl.classList.remove('auction__redirect-banner--success', 'auction__redirect-banner--error');
        this.bannerEl.classList.add(`auction__redirect-banner--${variant}`);
        this.bannerEl.hidden = false;
      }

      onBannerDismiss() {
        this.bannerEl.hidden = true;
      }

      startPolling() {
        if (this.pollTimer) return;
        this.pollTimer = setInterval(this.fetchState.bind(this), this.pollIntervalMs);
      }

      stopPolling() {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }

      onVisibilityChange() {
        if (document.hidden) {
          this.stopPolling();
        } else {
          this.fetchState();
          this.startPolling();
        }
      }

      onTimerExpired() {
        this.fetchState();
      }

      onWatchClick() {
        fetch(AuctionIdentity.withToken(this.dataset.watchUrl), {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((data) => {
            AuctionIdentity.set(data.bidderToken);
            this.watchButton.setAttribute('aria-pressed', String(data.watching));
            this.watchButton.classList.toggle('is-watching', data.watching);
            this.watcherCountEl.textContent = auctionFillTemplate(this.watchingTemplate, { COUNT: data.watcherCount });
          })
          .catch((e) => console.error(e));
      }

      fetchState() {
        return fetch(AuctionIdentity.withToken(this.dataset.stateUrl), {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((data) => this.render(data))
          .catch((e) => console.error(e));
      }

      render(data) {
        this.loadingEl.classList.add('hidden');

        if (data.status !== 'live') {
          this.endStateEl.classList.remove('hidden');
          this.renderEndState(data);

          // Sold/paid, and passed-with-bids (reserve not met) still have a real
          // record worth keeping visible - the final price, who bid what, and
          // the shipping/notices info a winner still needs. Only scheduled,
          // paused, and a bid-less passed lot fall back to hiding .auction__content
          // entirely, since there's nothing meaningful there yet/again.
          const hasRecord = data.status === 'sold' || data.status === 'paid' || (data.status === 'passed' && data.bidCount > 0);

          if (hasRecord) {
            this.contentEl.classList.remove('hidden');
            // The countdown and bid form don't apply once the outcome is
            // decided - hiding them (rather than the whole block) is what lets
            // price/shipping/history/notices stay visible below the banner.
            this.timerEl.classList.add('hidden');
            this.bidFormEl.classList.add('hidden');
            this.yourBidEl.hidden = true;
            this.leadingNoteEl.hidden = true;
            // The sole-bidder "price is hidden until contested" notes describe
            // an in-progress dynamic that's no longer true - the price shown
            // below is now the final, fully revealed settlement amount.
            this.historyNoteEl.hidden = true;
            this.currentBidEl.textContent = data.currentBidFormatted || auctionFormatMoney(data.currentBid * 100, this.dataset.moneyFormat);
            this.bidCountEl.textContent = auctionFillTemplate(this.bidCountTemplate, { COUNT: data.bidCount });
            this.endDateEl.textContent = auctionFillTemplate(this.endedOnTemplate, { DATE: auctionFormatDateTime(data.endsAt) });
            this.renderHistory(data.bidHistory || []);
          } else {
            this.contentEl.classList.add('hidden');
          }

          // Only a genuinely terminal state (sold/paid/passed/needsManualReview)
          // stops polling - 'scheduled' and 'paused' can both become 'live'
          // again with no action from this shopper (the start time arrives, or
          // the merchant resumes it), so the page needs to keep checking
          // instead of getting stuck showing stale copy until a manual refresh.
          if (data.status !== 'scheduled' && data.status !== 'paused') {
            this.stopPolling();
          }
          return;
        }

        this.contentEl.classList.remove('hidden');
        this.timerEl.classList.remove('hidden');
        this.bidFormEl.classList.remove('hidden');

        // A brief, quiet color flash when the price actually moves - a subtle
        // "something just happened" cue instead of the number silently
        // changing between poll ticks. Never fires on the very first render
        // (this.lastCurrentBid is still undefined then) or when a poll
        // returns the same price as before.
        if (this.lastCurrentBid != null && data.currentBid !== this.lastCurrentBid) {
          this.currentBidEl.classList.remove('auction__current-bid--updated');
          // eslint-disable-next-line no-void -- forces a reflow so re-adding
          // the class immediately after removing it still restarts the animation
          void this.currentBidEl.offsetWidth;
          this.currentBidEl.classList.add('auction__current-bid--updated');
        }
        this.lastCurrentBid = data.currentBid;

        this.currentBidEl.textContent = data.currentBidFormatted || auctionFormatMoney(data.currentBid * 100, this.dataset.moneyFormat);

        // The public price stays pinned while a bidder is uncontested (never
        // reveals their ceiling to other shoppers) - but the bidder themselves
        // still needs to see what they actually committed to.
        const yourBidAmount = data.yourBid != null ? data.yourBidFormatted || auctionFormatMoney(data.yourBid * 100, this.dataset.moneyFormat) : null;
        this.yourBidEl.hidden = yourBidAmount == null;
        if (yourBidAmount != null) {
          this.yourBidEl.textContent = auctionFillTemplate(this.yourBidTemplate, { AMOUNT: yourBidAmount, ALIAS: data.yourMaskedLabel || '' });
        }

        this.leadingNoteEl.hidden = !data.isSoleLeadingBidder;
        if (data.isSoleLeadingBidder && yourBidAmount != null) {
          this.leadingNoteTextEl.textContent = auctionFillTemplate(this.leadingNoteTemplate, { AMOUNT: yourBidAmount });
        }

        this.bidCountEl.textContent = auctionFillTemplate(this.bidCountTemplate, { COUNT: data.bidCount });
        this.watcherCountEl.textContent = auctionFillTemplate(this.watchingTemplate, { COUNT: data.watcherCount });
        this.watchButton.setAttribute('aria-pressed', String(Boolean(data.isWatching)));
        this.watchButton.classList.toggle('is-watching', Boolean(data.isWatching));

        this.timerEl.setEndsAt(data.endsAt);
        // The countdown alone doesn't say WHEN it ends in absolute terms - a
        // shopper checking back later, or planning around it, needs the real
        // date/time too, not just a relative duration that resets meaning
        // every time they look at it.
        this.endDateEl.textContent = auctionFillTemplate(this.endDateTemplate, { DATE: auctionFormatDateTime(data.endsAt) });
        this.bidFormEl.updateFromState(data);

        // isSoleLeadingBidder (this viewer IS the one bidder) gets the
        // personalized wording; any other visitor still needs to know why
        // every row below looks identical, so isUncontested (true for anyone)
        // falls back to the generic version instead of showing nothing.
        if (data.isSoleLeadingBidder) {
          this.historyNoteEl.textContent = this.historyNoteVariants.sole;
          this.historyNoteEl.hidden = false;
        } else if (data.isUncontested) {
          this.historyNoteEl.textContent = this.historyNoteVariants.uncontested;
          this.historyNoteEl.hidden = false;
        } else {
          this.historyNoteEl.hidden = true;
        }
        this.renderHistory(data.bidHistory || []);

        publish(PUB_SUB_EVENTS.auctionUpdate, { source: 'auction-lot', data });
      }

      renderHistory(bids) {
        this.historyListEl.innerHTML = '';
        bids.forEach((bid) => {
          const row = this.historyRowTemplate.content.cloneNode(true);
          row.querySelector('[data-row-name]').textContent = bid.maskedLabel;
          row.querySelector('[data-row-amount]').textContent =
            bid.amountFormatted || auctionFormatMoney(bid.amount * 100, this.dataset.moneyFormat);
          row.querySelector('[data-row-time]').textContent = auctionFormatDateTime(bid.placedAt);
          this.historyListEl.appendChild(row);
        });
      }

      renderEndState(data) {
        Object.keys(this.endStateVariants).forEach((key) => {
          this.endStateVariants[key].hidden = true;
        });

        // 'paid' is the same outcome as 'sold' from the shopper's point of view
        // (the sale already happened) - it only exists so the backend can tell
        // the two apart internally once the seller confirms payment.
        if (data.status === 'sold' || data.status === 'paid') {
          this.soldBannerSubtitleEl.textContent = auctionFillTemplate(this.soldToTemplate, {
            BIDDER: data.winnerMaskedLabel || '',
            AMOUNT: data.currentBidFormatted || auctionFormatMoney(data.currentBid * 100, this.dataset.moneyFormat),
          });
          this.endStateVariants.sold.hidden = false;
          return;
        }

        if (data.status === 'scheduled') {
          this.endStateVariants.scheduled.textContent = auctionFillTemplate(this.notStartedTemplate, {
            DATE: auctionFormatDateTime(data.startsAt),
          });
          this.endStateVariants.scheduled.hidden = false;
          return;
        }

        if (data.status === 'passed' && data.bidCount > 0) {
          this.endStateVariants.not_sold.hidden = false;
          return;
        }

        if (data.status === 'paused') {
          this.endStateVariants.paused.hidden = false;
          return;
        }

        this.endStateVariants.no_bids.hidden = false;
      }
    }
  );
}
